/**
 * Fill — what to assign from Ready to Assign, and assigning it in YNAB.
 *
 * YNAB's auto-assign fills targets. This fills by what the transactions say first:
 * money already spent, then bills about to leave, then this month's targets — and when
 * all of that is covered, the bills further out a fortnight at a time, and then the
 * rest of each goal. The plan is `@ynab/core`'s `planFillDetailed`; this screen shows
 * it, lets any line be left out, and sends the rest to YNAB.
 *
 * One category can appear more than once — a bill coming up in November and, later,
 * the rest of the yearly target it belongs to. Each line ticks on its own; applying
 * adds up what is ticked per category, because YNAB takes one figure per category.
 *
 * Pressing the button changes the real budget, so it takes two presses. The first
 * shows what is about to happen; only the second writes. The API then checks YNAB has
 * not moved since the plan was drawn and refuses if it has — a plan made from stale
 * figures would overwrite whatever either of you assigned in YNAB meanwhile.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@ynab/api-client'
import { coverage } from '@ynab/core'
import { useState } from 'react'
import {
  type ApplyFillResponse,
  applyFill,
  type FillAllocation,
  type FillResponse,
  fetchFill,
  money,
  queryKeys,
} from '../api.ts'
import { fmt, shortDate } from '../lib/format.ts'

/** Must match the API's own cap — YNAB's hourly request limit is shared. */
const MAX_CATEGORIES = 40

const keyOf = (a: FillAllocation) => `${a.categoryId}:${a.tier}:${a.window ?? ''}`

/** "Nov 2026" — a goal's due date is a month to aim for, not a day. */
function monthYear(isoDate: string): string {
  return new Intl.DateTimeFormat('en-NZ', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDate}T00:00:00Z`))
}

export function Fill() {
  const queryClient = useQueryClient()
  const fill = useQuery({ queryKey: queryKeys.fill, queryFn: fetchFill })
  // Lines left out of this fill. Everything the plan funds starts ticked.
  const [skipped, setSkipped] = useState<Set<string>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const [outcome, setOutcome] = useState<ApplyFillResponse['result'] | null>(null)

  const data = fill.data
  const chosen = (data?.allocations ?? []).filter((a) => !skipped.has(keyOf(a)))
  const total = chosen.reduce((sum, a) => sum + a.amount, 0)

  // YNAB takes one assigned figure per category, so ticked lines are added up per category.
  const perCategory = new Map<string, { allocation: FillAllocation; amount: number }>()
  for (const allocation of chosen) {
    const entry = perCategory.get(allocation.categoryId)
    if (entry) entry.amount += allocation.amount
    else perCategory.set(allocation.categoryId, { allocation, amount: allocation.amount })
  }
  const tooMany = perCategory.size > MAX_CATEGORIES

  const apply = useMutation({
    mutationFn: (plan: FillResponse) =>
      applyFill({
        month: plan.month,
        expectedReadyToAssign: plan.readyToAssign,
        lines: [...perCategory.values()].map(({ allocation, amount }) => ({
          categoryId: allocation.categoryId,
          amount,
          expectedBudgeted: allocation.budgeted,
          // The most pressing reason it was given anything — allocations come in plan order.
          tier: allocation.tier,
        })),
      }),
    onSuccess: (response) => {
      setOutcome(response.result)
      setSkipped(new Set())
      queryClient.setQueryData(queryKeys.fill, response.plan)
      // Every screen reads the mirror, and the mirror just moved.
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'fill' })
    },
    onError: (error) => {
      // A 409 means YNAB moved under the plan. The plan on screen is stale, so replace it.
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.fill })
      }
    },
    onSettled: () => setConfirming(false),
  })

  const toggle = (id: string) =>
    setSkipped((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <section className="flex max-w-[960px] flex-col gap-5 px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 font-display text-[34px] font-normal leading-none">Fill</h2>
        <p className="m-0 max-w-[620px] text-[14px] text-muted">
          What to do with Ready to Assign, by what the transactions say first: cover what is already
          spent, then what is scheduled to leave, and only then fund targets.
        </p>
      </div>

      {fill.isPending && <p className="text-[14px] text-muted">Drawing up the plan…</p>}
      {fill.isError && (
        <p className="rounded-card bg-blush-soft p-4 text-[14px] text-blush-deep">
          {fill.error.message}
        </p>
      )}

      {outcome && <Outcome outcome={outcome} onDismiss={() => setOutcome(null)} />}

      {data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-card px-5 py-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                Ready to assign in YNAB
              </span>
              <span className="font-display text-[30px] leading-none">
                {fmt(data.readyToAssign)}
              </span>
            </div>
            {data.readyToAssign > 0 && (
              <div className="flex flex-col items-end gap-1 text-[13px]">
                <span>
                  This plan assigns <span className="font-medium">{fmt(total)}</span>
                </span>
                {data.stillWanted > 0 && (
                  <span className="text-muted">
                    {fmt(data.stillWanted)} more is wanted than there is to give
                  </span>
                )}
              </div>
            )}
          </div>

          {data.readyToAssign <= 0 ? (
            <p className="text-[14px] text-muted">
              {data.readyToAssign < 0
                ? `YNAB has ${fmt(-data.readyToAssign)} more assigned than you have. Nothing to fill — take it back from a category in YNAB first.`
                : 'Nothing to assign. When money lands in Ready to Assign, this is where it goes.'}
            </p>
          ) : data.allocations.length === 0 ? (
            <p className="text-[14px] text-muted">
              Nothing is overspent, due, short of its target or left to save towards, so there is
              nothing to ask for.
            </p>
          ) : (
            <>
              {sectionsOf(data).map((section) => (
                <div
                  key={section.key}
                  className="flex flex-col rounded-card border border-line bg-card px-4 py-3"
                >
                  <span className="text-[13.5px] font-medium">{section.title}</span>
                  <span className="pb-1.5 text-[12px] text-muted">{section.caption}</span>
                  {section.allocations.map((allocation) => (
                    <Line
                      key={keyOf(allocation)}
                      allocation={allocation}
                      given={perCategory.get(allocation.categoryId)?.amount ?? 0}
                      included={!skipped.has(keyOf(allocation))}
                      onToggle={() => toggle(keyOf(allocation))}
                    />
                  ))}
                  {section.unmet > 0 && (
                    <span className="border-t border-line-faint pt-1.5 text-[12px] text-muted">
                      {section.unmetNote ??
                        `${fmt(section.unmet)} more is wanted here than there was to give.`}
                    </span>
                  )}
                </div>
              ))}

              <div className="sticky bottom-24 flex flex-wrap items-center justify-end gap-3 rounded-card border border-line bg-paper px-4 py-3 shadow-[var(--shadow-pop)] lg:bottom-4">
                {apply.isError && (
                  <span className="mr-auto text-[13px] text-blush-deep">{apply.error.message}</span>
                )}
                {tooMany && (
                  <span className="mr-auto text-[13px] text-blush-deep">
                    That is {perCategory.size} categories; untick some to bring it to{' '}
                    {MAX_CATEGORIES}, which is as many as one fill sends to YNAB.
                  </span>
                )}
                {confirming ? (
                  <>
                    <span className="text-[13px]">
                      This changes your YNAB budget: {fmt(total)} across {perCategory.size}{' '}
                      {perCategory.size === 1 ? 'category' : 'categories'}.
                    </span>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      disabled={apply.isPending}
                      className="rounded-full border border-line bg-card px-4 py-2.5 text-[13px] text-ink hover:bg-sand"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => apply.mutate(data)}
                      disabled={apply.isPending}
                      className="rounded-full bg-cta px-4 py-2.5 text-[13px] font-medium text-cta-fg hover:bg-cta-hover disabled:opacity-50"
                    >
                      {apply.isPending ? 'Assigning in YNAB…' : 'Yes, assign it'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    disabled={total <= 0 || tooMany}
                    className="rounded-full bg-cta px-4 py-2.5 text-[13px] font-medium text-cta-fg hover:bg-cta-hover disabled:opacity-40"
                  >
                    Assign {fmt(total)} in YNAB
                  </button>
                )}
              </div>
            </>
          )}

          {data.recent.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="m-0 font-display text-[20px] font-normal">Assigned from here</h3>
              {data.recent.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-3 border-t border-line-faint py-1.5 text-[12.5px]"
                >
                  <span className="w-[52px] text-muted">
                    {shortDate(new Date(row.createdAt).toISOString().slice(0, 10))}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {row.member} · {row.category}
                    {!row.ok && <span className="text-blush-deep"> · failed: {row.error}</span>}
                  </span>
                  <span className={row.ok ? 'text-ink-dim' : 'text-muted line-through'}>
                    {fmt(row.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}

/**
 * The plan in the order it was drawn: this period, then each window ahead, then goals.
 * A section with nothing in it is left out, but its unmet figure still shows where the
 * money ran out before reaching it.
 */
function sectionsOf(plan: FillResponse) {
  const period = [
    {
      key: 'overspent',
      tier: 'overspent',
      title: 'Already spent',
      caption: 'Money that has left the bank. Uncovered, it comes out of next month.',
    },
    {
      key: 'imminent',
      tier: 'imminent',
      title: 'About to leave',
      caption: `Scheduled in YNAB within the next ${plan.horizonDays} days.`,
    },
    {
      key: 'planned',
      tier: 'planned',
      title: 'This month’s targets',
      caption: 'What YNAB’s targets still need this month.',
    },
  ] as const
  const sections = period.map((section) => ({
    ...section,
    allocations: plan.allocations.filter((a) => a.tier === section.tier),
    unmet: plan.unmet[section.tier],
    unmetNote: null as string | null,
  }))

  const ahead = plan.windows.map((window) => ({
    key: `ahead-${window.index}`,
    title: `Due ${shortDate(window.from)} – ${shortDate(window.through)}`,
    caption: 'Every bill for this period is covered, so this holds the next ones ahead of time.',
    allocations: plan.allocations.filter((a) => a.tier === 'ahead' && a.window === window.index),
    unmet: 0,
    unmetNote: null as string | null,
  }))
  // The unmet total for the look-ahead is one figure; it belongs after the last window
  // anything reached, which is where the money ran out.
  const lastAhead = [...ahead].reverse().find((section) => section.allocations.length > 0)
  const lastWindow = plan.windows[plan.windows.length - 1]
  if (lastAhead && lastWindow) {
    lastAhead.unmet = plan.unmet.ahead
    // One figure for every window, so it says so rather than sitting under one fortnight.
    lastAhead.unmetNote = `Bills through ${shortDate(lastWindow.through)} still want ${fmt(plan.unmet.ahead)} more than there was to give.`
  }

  const goals = {
    key: 'goal',
    title: 'Goals',
    caption:
      'Bills for the next three months are covered. The rest of each target, soonest due first.',
    allocations: plan.allocations.filter((a) => a.tier === 'goal'),
    unmet: plan.unmet.goal,
    unmetNote: null as string | null,
  }

  return [...sections, ...ahead, goals].filter((section) => section.allocations.length > 0)
}

/**
 * When the category's money runs out, after what is ticked is assigned — the answer to
 * "what are we actually doing here". Worked out from what is ticked now, so it moves as
 * lines are left out.
 */
function lastsUntil(a: FillAllocation, given: number): string | null {
  if (a.bills.length === 0) {
    // Nothing scheduled, so the target is the only measure there is.
    if (a.goalLeft <= 0) return null
    const left = Math.max(0, a.goalLeft - given)
    return left > 0
      ? `${fmt(left)} of the target still to go${a.goalDue ? ` before ${monthYear(a.goalDue)}` : ''}`
      : `Target met${a.goalDue ? ` for ${monthYear(a.goalDue)}` : ''}`
  }
  const { coveredThrough, firstShort } = coverage(money(a.available + given), a.bills)
  if (!firstShort) {
    const last = a.bills[a.bills.length - 1]
    return `Every bill covered, to ${last ? shortDate(last.date) : 'the end of the year'}`
  }
  if (!coveredThrough) {
    return `Not enough for ${shortDate(firstShort.date)} yet — ${fmt(firstShort.short)} short`
  }
  return firstShort.short >= firstShort.amount
    ? `Filled until ${shortDate(firstShort.date)} — nothing yet for that day’s ${fmt(firstShort.amount)}`
    : `Filled until ${shortDate(firstShort.date)} — ${fmt(firstShort.short)} short of that day’s ${fmt(firstShort.amount)}`
}

function Line({
  allocation,
  given,
  included,
  onToggle,
}: {
  allocation: FillAllocation
  /** Everything ticked for this category, across every line it appears on. */
  given: number
  included: boolean
  onToggle: () => void
}) {
  const next = allocation.bills[0]
  const until = lastsUntil(allocation, given)
  const a = allocation
  const reason =
    a.tier === 'overspent'
      ? `${fmt(-a.available)} overspent${a.scheduledSoon > 0 ? `, ${fmt(a.scheduledSoon)} more due` : ''}`
      : a.tier === 'imminent'
        ? `${fmt(a.scheduledSoon)} due, holds ${fmt(a.available)}`
        : a.tier === 'planned'
          ? `target needs ${fmt(a.underfunded)} this month`
          : a.tier === 'ahead'
            ? `${fmt(a.scheduledInWindow)} due`
            : `${fmt(a.goalLeft)} left${a.goalDue ? `, due ${monthYear(a.goalDue)}` : ''}`
  return (
    <label className="flex cursor-pointer items-start gap-2.5 border-t border-line-faint py-1.5 text-[13px] first-of-type:border-t-0">
      <input
        type="checkbox"
        checked={included}
        onChange={onToggle}
        className="mt-[3px] accent-[var(--color-cta)]"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">
          {a.name}
          <span className="text-muted"> · {reason}</span>
        </span>
        {(next || until) && (
          <span className="truncate text-[11.5px] text-muted">
            {next && `Next ${fmt(next.amount)} on ${shortDate(next.date)}`}
            {next && until && ' · '}
            {until}
          </span>
        )}
      </span>
      <span className={included ? 'text-sky-deep' : 'text-muted line-through'}>
        {fmt(a.amount)}
      </span>
    </label>
  )
}

function Outcome({
  outcome,
  onDismiss,
}: {
  outcome: ApplyFillResponse['result']
  onDismiss: () => void
}) {
  const failed = outcome.results.filter((r) => !r.ok)
  return (
    <div
      className={`flex flex-col gap-1 rounded-card border px-5 py-4 ${
        failed.length > 0 ? 'border-blush bg-blush-soft' : 'border-sky-line bg-sky-soft'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[15px]">
          Assigned {fmt(outcome.assigned)} in YNAB
          {failed.length > 0 ? `, but ${failed.length} did not go through:` : '.'}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-[13px] text-muted hover:text-ink"
        >
          ✕
        </button>
      </div>
      {failed.map((r) => (
        <span key={r.categoryId} className="text-[13px] text-blush-text">
          {r.name}, {fmt(r.amount)} — {r.error}
        </span>
      ))}
    </div>
  )
}
